import { Period } from "../../types";

export const PERIOD_NAME_MAX = 30;

export type PeriodFieldErrors = Partial<Record<"name" | "startTime" | "endTime", string>>;

/**
 * Bell schedule rules (the backend checks the same): name required, up to 30 characters and unique;
 * start and end required, end after start; no two periods overlap (one may start when another ends).
 * Returns errors keyed by the row's index in `periods`; empty when valid.
 */
export const validatePeriods = (periods: Period[]): Record<number, PeriodFieldErrors> => {
  const errors: Record<number, PeriodFieldErrors> = {};
  const add = (index: number, field: keyof PeriodFieldErrors, message: string) => {
    errors[index] = { ...errors[index], [field]: errors[index]?.[field] ?? message };
  };

  periods.forEach((period, index) => {
    const name = period.name.trim();
    if (!name) add(index, "name", "Required");
    else if (name.length > PERIOD_NAME_MAX) add(index, "name", `Up to ${PERIOD_NAME_MAX} characters`);
    else if (periods.some((other, i) => i !== index && other.name.trim().toLowerCase() === name.toLowerCase())) {
      add(index, "name", "Name already used");
    }

    if (!period.startTime) add(index, "startTime", "Required");
    if (!period.endTime) add(index, "endTime", "Required");
    if (period.startTime && period.endTime && period.endTime <= period.startTime) {
      add(index, "endTime", "Must be after start");
    }
  });

  // overlaps: only between rows whose own times are valid
  const timed = periods
    .map((period, index) => ({ period, index }))
    .filter(({ period }) => period.startTime && period.endTime && period.endTime > period.startTime)
    .sort((a, b) => a.period.startTime.localeCompare(b.period.startTime));
  for (let i = 1; i < timed.length; i++) {
    const previous = timed[i - 1];
    const current = timed[i];
    if (current.period.startTime < previous.period.endTime) {
      add(current.index, "startTime", `Overlaps ${previous.period.name.trim() || "another period"}`);
    }
  }

  return errors;
};
